export interface NavigationLink {
  to: string;
  label: string;
  roles?: string[];
  requiresAuth?: boolean;
  orgAware?: boolean;
}

export interface NavigationSection {
  title: string;
  roles?: string[];
  links: NavigationLink[];
}

const normalizeRole = (role: string): string => role.trim().toLowerCase();

const collectStringValues = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).map(normalizeRole);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return [normalizeRole(value)];
  }

  return [];
};

export const getUserRoles = (user: unknown): string[] => {
  if (!user || typeof user !== 'object') {
    return [];
  }

  const record = user as Record<string, unknown>;
  const appMetadata = record['app_metadata'];
  const metadataObject = appMetadata && typeof appMetadata === 'object' && !Array.isArray(appMetadata)
    ? (appMetadata as Record<string, unknown>)
    : undefined;

  const values = [
    record.roles,
    record['roles'],
    record['https://exammasters.com/roles'],
    record['https://exammasters.com/organization-roles'],
    record['https://exammasters.com/organizationRoles'],
    appMetadata,
    metadataObject?.roles,
    metadataObject?.organizationRoles,
  ];

  const roles = values.flatMap((value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = value as Record<string, unknown>;
      return collectStringValues(nested.roles);
    }

    return collectStringValues(value);
  });

  return Array.from(new Set(roles));
};

export const hasAnyRole = (availableRoles: string[], requiredRoles?: string[]): boolean => {
  if (!requiredRoles?.length) {
    return true;
  }

  if (!availableRoles.length) {
    return false;
  }

  const normalizedAvailable = new Set(availableRoles.map(normalizeRole));
  return requiredRoles.some((role) => normalizedAvailable.has(normalizeRole(role)));
};

export const getMembershipRoles = (organizations: unknown): string[] => {
  if (!Array.isArray(organizations)) {
    return [];
  }

  return organizations.flatMap((organization) => {
    if (!organization || typeof organization !== 'object') {
      return [];
    }

    const record = organization as Record<string, unknown>;
    return collectStringValues(record.roles);
  });
};

export const mergeRoles = (...roleSets: string[][]): string[] => {
  const merged = roleSets.flatMap((roles) => roles);
  return Array.from(new Set(merged.map(normalizeRole)));
};

export const canAccessNavItem = (item: NavigationLink, isAuthenticated: boolean, roles: string[]): boolean => {
  if (item.requiresAuth && !isAuthenticated) {
    return false;
  }

  if (!item.roles?.length) {
    return true;
  }

  if (!isAuthenticated) {
    return false;
  }

  return roles.length === 0 ? true : hasAnyRole(roles, item.roles);
};

export const filterNavItems = (items: NavigationLink[], isAuthenticated: boolean, roles: string[]): NavigationLink[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    if (!canAccessNavItem(item, isAuthenticated, roles)) {
      return false;
    }

    const normalizedTo = (item.to || '').trim().toLowerCase();
    if (!normalizedTo || seen.has(normalizedTo)) {
      return false;
    }

    seen.add(normalizedTo);
    return true;
  });
};

export const filterNavSections = (sections: NavigationSection[], isAuthenticated: boolean, roles: string[]): NavigationSection[] => {
  return sections
    .map((section) => ({
      ...section,
      links: filterNavItems(section.links, isAuthenticated, roles),
    }))
    .filter((section) => section.links.length > 0 && (!section.roles?.length || hasAnyRole(roles, section.roles)));
};

export const adminDropdownLinks: NavigationLink[] = [
  { to: '/manage-organizations', label: 'Manage Organizations', roles: ['superadmin', 'owner'] },
  { to: '/manage-organization-memberships', label: 'Organization Memberships', roles: ['superadmin', 'owner'] },
  { to: '/manage-organization-members-and-roles', label: 'Members & Roles', roles: ['superadmin', 'owner'] },
  { to: '/manage-organization-membership-roles', label: 'Membership Roles', roles: ['superadmin', 'owner'] },
  { to: '/manage-organization-requests', label: 'Requests & Invites', roles: ['superadmin', 'owner', 'principal'] },
  { to: '/manage-platform-user-roles', label: 'Platform User Roles', roles: ['superadmin'] },
  { to: '/manage-categories', label: 'Manage Categories', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-courses', label: 'Manage Courses', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-course-pricing', label: 'Course Pricing', roles: ['superadmin', 'owner'] },
  { to: '/manage-classrooms', label: 'Manage Classrooms', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-assignments', label: 'Manage Assignments', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-exams', label: 'Manage Exams', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-questions', label: 'Manage Questions', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-diagrams', label: 'Manage Diagrams', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-category-diagrams', label: 'Manage Category Diagrams', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-dynamic-components', label: 'Manage Dynamic Components', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-site-content', label: 'Manage Site Content', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-free-resources', label: 'Manage Free Resources', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/manage-documents', label: 'Manage Documents', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/admin/course-workspace', label: 'Course Workspace Wizard', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
  { to: '/books', label: 'Books', roles: ['superadmin', 'owner', 'principal', 'teacher'] },
];

export const settingsDropdownLinks: NavigationLink[] = [
  { to: '/analytics?pre=1', label: 'Switch Organization', requiresAuth: true },
  { to: '/my-enrollments', label: 'My Enrollments', requiresAuth: true },
  { to: '/profile', label: 'My Profile', requiresAuth: true },
  { to: '/settings', label: 'Settings', requiresAuth: true },
];

export const studentNavigationSections: NavigationSection[] = [
  {
    title: 'Overview',
    links: [
      { to: '/analytics', label: 'Dashboard' },
    ],
  },
  {
    title: 'Learning',
    links: [
      { to: '/courses', label: 'Courses' },
      { to: '/practice', label: 'Practice' },
      { to: '/practice-tests', label: 'Practice Tests' },
    ],
  },
  {
    title: 'My Work',
    links: [
      { to: '/assignments', label: 'Assignments' },
      { to: '/my-sessions', label: 'My Sessions' },
      { to: '/my-enrollments', label: 'My Enrollments' },
    ],
  },
  {
    title: 'Account',
    links: [
      { to: '/profile', label: 'Profile' },
      { to: '/settings', label: 'Settings' },
      { to: '/billing', label: 'Billing' },
    ],
  },
];

export const adminNavigationSections: NavigationSection[] = [
  {
    title: 'Overview',
    roles: ['superadmin', 'owner', 'principal'],
    links: [
      { to: '/admin', label: 'Dashboard' },
    ],
  },
  {
    title: 'Teacher',
    roles: ['teacher'],
    links: [
      { to: '/teacher-dashboard', label: 'Dashboard' },
      { to: '/manage-students', label: 'Students' },
      { to: '/manage-assignments', label: 'Assignments' },
      { to: '/manage-submissions', label: 'Submissions' },
      // { to: '/manage-exams', label: 'Exams' },
      { to: '/manage-schedule', label: 'Manage Schedule' },
      { to: '/manage-sessions/all', label: 'Manage Sessions' },
      { to: '/manage-categories-v2?scope=teacher', label: 'Categories V2' },
      { to: '/manage-questions', label: 'Questions' },
    ],
  },
  {
    title: 'Onboarding',
    roles: ['superadmin', 'owner', 'principal'],
    links: [
      { to: '/manage-organization-requests', label: 'Requests & Invites' },
      { to: '/manage-staff', label: 'Staff & Leadership' },
      { to: '/manage-teachers', label: 'Teachers' },
      { to: '/manage-students', label: 'Students' },
      { to: '/manage-teacher-course-assignments', label: 'Teacher Course Access' },
      { to: '/manage-teacher-student-links', label: 'Teacher–Student Links' },
    ],
  },
  {
    title: 'Assessments',
    roles: ['superadmin', 'owner', 'principal'],
    links: [
      { to: '/manage-assignments', label: 'Assignments' },
      { to: '/manage-submissions', label: 'Submissions' },
      { to: '/manage-exams', label: 'Exams' },
      { to: '/manage-schedule', label: 'Manage Schedule' },
      { to: '/manage-sessions/all', label: 'Manage Sessions' },
    ],
  },
  {
    title: 'Organization',
    roles: ['superadmin', 'owner', 'principal'],
    links: [
      { to: '/manage-organizations', label: 'Organizations' },
      { to: '/manage-organization-members-and-roles', label: 'Members & Roles', roles: ['superadmin', 'owner'] },
      { to: '/manage-courses', label: 'Courses' },
      { to: '/manage-classrooms', label: 'Classrooms' },
      { to: '/manage-email-templates', label: 'Email Templates' },
      { to: '/manage-site-content', label: 'Site Content' },
    ],
  },
  {
    title: 'Reports',
    roles: ['superadmin', 'owner', 'principal'],
    links: [
      { to: '/reports/teacher-hours', label: 'Teacher Hours' },
      { to: '/reports/student-sessions', label: 'Student Sessions' },
    ],
  },
  {
    title: 'Learning Content',
    roles: ['superadmin', 'owner', 'principal'],
    links: [
      { to: '/manage-categories-v2?scope=admin', label: 'Categories V2' },
      { to: '/manage-questions', label: 'Questions' },
    ],
  },
  {
    title: 'Super Admin',
    roles: ['superadmin'],
    links: [
      { to: '/super-admin', label: 'Overview' },
      { to: '/super-admin/platform-users', label: 'Platform Users' },
      { to: '/manage-platform-user-roles', label: 'Platform User Roles' },
      // { to: '/manage-organization-memberships', label: 'Organization Memberships' },
      // { to: '/manage-organization-membership-roles', label: 'Membership Roles' },
      // { to: '/manage-organization-members-and-roles', label: 'Members & Roles' },
      // { to: '/manage-organization-requests', label: 'Requests & Invites' },
      { to: '/manage-organizations', label: 'Organizations' },
      { to: '/admin/course-workspace', label: 'Course Workspace Wizard' },
    ],
  },
  {
    title: 'Resources',
    roles: ['superadmin'],
    links: [
      { to: '/manage-free-resources', label: 'Free Resources' },
      { to: '/manage-documents', label: 'Documents' },
      { to: '/books', label: 'Books' },
      { to: '/manage-diagrams', label: 'Diagrams' },
      { to: '/manage-category-diagrams', label: 'Category Diagrams' },
      { to: '/manage-dynamic-components', label: 'Dynamic Components' },
    ],
  },
];

export const staffNavigationSections = adminNavigationSections;

export const navigationSectionsByAudience = {
  student: studentNavigationSections,
  admin: adminNavigationSections,
  staff: adminNavigationSections,
} as const;
